const SYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;

export const getSubscriptionStorageKey = (companyId: number): string =>
  `sub_${companyId}`;

export const getSubscriptionSyncStorageKey = (companyId: number): string =>
  `sub_sync_${companyId}`;

export const markPushSubscriptionSynced = (companyId: number): void => {
  localStorage.setItem(getSubscriptionSyncStorageKey(companyId), String(Date.now()));
};

export const isDevelopmentEnvironment = (): boolean =>
  process.env.NODE_ENV === "development" ||
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

export const isPushSubscriptionEnabled = (companyId: number): boolean =>
  localStorage.getItem(getSubscriptionStorageKey(companyId)) === "enabled";

export const shouldSyncPushSubscription = (companyId: number): boolean => {
  const lastSync = localStorage.getItem(getSubscriptionSyncStorageKey(companyId));
  if (!lastSync) {
    return true;
  }

  const lastSyncTime = Number(lastSync);
  if (Number.isNaN(lastSyncTime)) {
    return true;
  }

  return Date.now() - lastSyncTime >= SYNC_INTERVAL_MS;
};

export const sendPushSubscriptionToBackend = async (
  companyId: number,
  subscription: PushSubscription,
  token?: string | null,
): Promise<Response> => {
  const authToken = token ?? localStorage.getItem("p_menu_token");

  return fetch(`/api/company/${companyId}/subscribe/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      userAgent: navigator.userAgent,
    }),
  });
};

export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

/**
 * Creates a PushSubscription for the given VAPID key.
 * If the browser already has a subscription under a different key,
 * it unsubscribes first, then resubscribes.
 */
export const createPushSubscription = async (
  registration: ServiceWorkerRegistration,
  vapidPublicKey: string,
): Promise<PushSubscription> => {
  const existingSubscription = await registration.pushManager.getSubscription();

  if (existingSubscription) {
    await existingSubscription.unsubscribe();
  }

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
};

export const syncPushSubscription = async (companyId: number): Promise<void> => {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const response = await sendPushSubscriptionToBackend(companyId, subscription);
      if (response.ok) {
        markPushSubscriptionSynced(companyId);
      }
      return;
    }
  } catch (error) {
    console.error("Push subscription sync failed:", error);
  }
};
